import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import useLocalStorage, { removeItem, STORAGE_KEYS } from "../hooks/useLocalStorage";
import Assignation from "../models/Assignation";
import AssignationTransferForm from "../models/AssignationTransferForm";
import AssignationService from "../services/AssignationService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import UserService from "../services/UserService";
import { Routes } from "../types/Routes";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import RoleUtils from "../utils/RoleUtils";
import useUpdateUrl from "./useUpdateUrl";

const useAssignationList = () => {
  const history = useHistory();

  const { paramAssignationExternalId } = useParams<{ paramAssignationExternalId?: string }>();

  const [projectsNames, setProjectNames] = useState<AutoCompleteUIDTO[]>([]);
  const [managerProjects, setManagerProjects] = useState<AutoCompleteUIDTO[]>([]);
  const [positionNames, setPositionNames] = useState<AutoCompleteUIDTO[]>([]);
  const [transferPositionNames, setTransferPositionNames] = useState<AutoCompleteUIDTO[]>([]);
  const [transferProject, setTransferProject] = useState<AutoCompleteUIDTO>(EMPTY_AUTOCOMPLETE);
  const [userNames, setUserNames] = useState<AutoCompleteUIDTO[]>([]);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [canView, setCanView] = useState<boolean>(false);
  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedAssignation, setSelectedAssignation] = useState<Assignation | undefined>(undefined);
  const [transferAssignation, setTransferAssignation] = useState<Assignation | undefined>(undefined);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [statusFilter, setStatusFilter] = useState<string>(AssignationStatusFilters.ACTIVE);
  const [reloadProjects, setReloadProjects] = useState(false);
  const [canManageProjects, setCanManageProjects] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [positionExternalId, setPositionExternalId] = useState("");
  const [assignationStartDate, setAssignationStartDate] = useState(Date.now().toString());
  const [assignationEndDate, setAssignationEndDate] = useState<string>();
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);

  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const status = searchParams.get("status");
  const assignedToMe = searchParams.get("assigned");
  const {updateUrl} = useUpdateUrl(Routes.ASSIGNATIONS, searchParams);

  useEffect(() => {
    if (positionExternalId && assignationStartDate) {
      validateDates(positionExternalId, assignationStartDate, assignationEndDate);
    }
  }, [assignationStartDate, positionExternalId, assignationEndDate]);

  useEffect(() => {
    getAllProjectsNames(assigned);
    setCanEdit(RoleUtils.isAdmin());
    setCanView(RoleUtils.isAdmin() || RoleUtils.isManager());
    checkIfUserIsManagerFromProject();
  }, [reloadProjects, assigned, update]);

  useEffect(() => {
    if (!RoleUtils.isAdmin()) {
      getManagerProjects();
    }
  }, []);

  useEffect(() => {
    if (project !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    if (status !== null) {
      setStatusFilter(status);
    } else {
      searchParams.set("status", AssignationStatusFilters.ACTIVE);
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
  }, [projectsNames]);

  useEffect(() => {
    if (canView && selectedProject && selectedProject.externalId !== "") {
      setTransferProject(selectedProject);
      PositionService.findPositionNamesByProjectId(selectedProject.externalId)
        .then((response) => {
          setPositionNames(response.getContent());
          setTransferPositionNames(response.getContent());
          updateUrl();
          checkIfUserIsManagerFromProject();
        })
        .catch((error) => traceo("Failed to get position names.", error, null));
    }
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTransferProjectChange = (project: AutoCompleteUIDTO) => {
    if (project.externalId !== "") {
      PositionService.findPositionNamesByProjectId(project.externalId)
        .then((response) => {
          setTransferPositionNames(response.getContent());
          setTransferProject(project);
        })
        .catch((error) => traceo("Failed to get position names.", error, null));
    }
  };

  const handleTransferAssignation = (formData: AssignationTransferForm) => {
    setIsLoading(true);
    AssignationService.transferAssignation(formData).then((response) => {
      if (response.isSuccess() && selectedProject) {
        NotificationService.info(`Transfer completed: ${response.getContentOrThrowError().userFullName}`);

        PositionService.findPositionNamesByProjectId(selectedProject.externalId)
          .then((response) => {
            setPositionNames(response.getContent());
            updateUrl();
            checkIfUserIsManagerFromProject();
          })
          .catch((error) => traceo("Failed to get position names.", error, null));

        setTransferAssignation(undefined);
      } else {
        NotificationService.error(`${response.getMessage()}`);
      }
    });
    setIsLoading(false);
  };

  const getManagerProjects = () => {
    ProjectService.findAllProjectsNames(false, true)
      .then((response) => {
        setManagerProjects(response.getContent());
      })
      .catch((error) => traceo("Error getting project names.", error, null));
  };

  const getAllProjectsNames = (assigned: boolean) => {
    setIsLoading(true);
    ProjectService.findAllProjectsNames(false, assigned)
      .then((response) => {
        setProjectNames(response.getContent());
      })
      .catch((error) => traceo("Error getting project names.", error, null))
      .finally(() => setIsLoading(false));

    UserService.findUserNames()
      .then((response) => {
        setUserNames(response.getContentOrThrowError());
      })
      .catch((error) => traceo("Error getting user names.", error, null))
      .finally(() => setIsLoading(false));
  };

  const checkIfUserIsManagerFromProject = async () => {
    if (!RoleUtils.isAdmin() && selectedProject && selectedProject.externalId !== "") {
      const response = await ProjectService.isUserCoreManagerFromProject(selectedProject.externalId, AuthService.getUser()?.externalId!);
      setCanEdit(response.succeeded());
    }
  };

  const handleOnDeleteAssignation = async () => {
    if (!selectedAssignation) return;

    try {
      await AssignationService.delete(selectedAssignation);

      setSelectedAssignation(undefined);
      setUpdate(Date.now());
      NotificationService.info("Assignation deleted successfully");
    } catch (error) {
      NotificationService.error("Error trying to delete a assignation.");
    }
  };

  const handleOnCreate = (instance: Assignation) => {
    if (!canEdit || selectedProject === undefined) {
      return undefined;
    }

    return AssignationService.create(instance)
      .then(() => {
        setUpdate(Date.now());
        NotificationService.info("Assignation created successfully");
        return true;
      })
      .catch((error) => {
        NotificationService.error(error.response.data.message);
        return false;
      });
  };

  const handleOnUpdate = (instance: Assignation) => {
    if (!canEdit) {
      return undefined;
    }

    return AssignationService.update(instance)
      .then(() => {
        setUpdate(Date.now());
        NotificationService.info("Assignation updated successfully");
        return true;
      })
      .catch((error) => {
        NotificationService.error(error.response.data.message);
        return false;
      });
  };

  const handleChangeAssignedProjectsByPosittion = () => {
    setAssigned(!assigned);
    setSelectedProject(EMPTY_AUTOCOMPLETE);
    searchParams.set("assigned", (!assigned).toString());
    setStoredProject(EMPTY_AUTOCOMPLETE);
    searchParams.delete("project");
    updateUrl();
  };

  const getAssignations = async (pageNumber: number, count: number) => {
    if (selectedProject === undefined) {
      return PagedResult.empty([]);
    }

    const result = await AssignationService.findByProjectExternalId(pageNumber, count, selectedProject?.externalId, statusFilter);
    const resultContent = result.getContent();

    if (!result.succeeded()) {
      NotificationService.error(`Error: ${resultContent}`);
      return PagedResult.empty([]);
    }

    return resultContent;
  };

  const validateDates = (positionExternalId: string, assignationStartDate: string, assignationEndDate: string | undefined) => {
    AssignationService.validateAssignationStartDate(positionExternalId, assignationStartDate, assignationEndDate || "")
      .then(() => {
        setDisableSaveButton(false);
      })
      .catch((error) => {
        NotificationService.error(error.response.data.message);
        setDisableSaveButton(true);
      });
  };

  return {
    history,
    projectsNames,
    positionNames,
    userNames,
    canEdit,
    update,
    setUpdate,
    selectedAssignation,
    transferAssignation,
    assigned,
    handleOnDeleteAssignation,
    handleChangeAssignedProjectsByPosittion,
    handleOnCreate,
    handleOnUpdate,
    setSelectedAssignation,
    setTransferAssignation,
    getAssignations,
    statusFilter,
    setStatusFilter,
    canManageProjects,
    isLoading,
    handleTransferAssignation,
    handleTransferProjectChange,
    transferPositionNames,
    transferProject,
    managerProjects,
    disableSaveButton,
    setPositionExternalId,
    setAssignationStartDate,
    setAssignationEndDate,
    setDisableSaveButton,
    selectedProject,
    setSelectedProject,
    storedProject,
    setStoredProject,
  };
};

export default useAssignationList;
